import { useState } from "react";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Brain, ThumbsUp, ThumbsDown, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

// AI Design Brain v1 — module list is data-driven so future modules (Design DNA,
// Creative Director, Design Critic) plug into the same panel without a redesign.
export const DesignBrainPanel = ({ config, enabled, onToggle }) => {
  if (!config?.enabled) return null;
  const modules = config.modules?.length
    ? config.modules
    : [{ key: "layout_engine", name: "Layout Engine", version: "v1", available: true }];
  const DESCRIPTIONS = {
    layout_engine: "GPT-5 plans hierarchy, typography & composition before rendering",
  };
  return (
    <div data-testid="design-brain-panel" className="rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/[0.05] p-3.5">
      <div className="flex items-center gap-2 mb-2.5">
        <Brain size={16} weight="fill" className="text-fuchsia-300" />
        <span className="text-xs uppercase tracking-[0.16em] text-zinc-300 font-bold">AI Design Brain</span>
        <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 ring-1 ring-fuchsia-400/30">v1</span>
      </div>
      {modules.map((m) => (
        <div key={m.key} data-testid={`brain-module-${m.key}`} className="flex items-start gap-3 py-1">
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold text-white leading-tight">
              {m.name} <span className="text-[9px] text-zinc-500 font-normal">{m.version}</span>
            </p>
            <p className="text-[10px] text-zinc-500 leading-snug mt-0.5">{DESCRIPTIONS[m.key] || ""}</p>
          </div>
          <Switch
            data-testid={`brain-toggle-${m.key}`}
            checked={enabled}
            onCheckedChange={onToggle}
            className="data-[state=checked]:bg-fuchsia-600"
          />
        </div>
      ))}
      <p className="text-[9.5px] text-zinc-600 mt-2 leading-snug">More modules coming: Design DNA · Creative Director · Design Critic</p>
    </div>
  );
};

// Thumbs up/down on a generated flyer — feeds the layout_feedback learning loop.
export const DesignFeedback = ({ jobId }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  if (!jobId) return null;

  const send = async (r, note) => {
    try {
      await api.post("/layout-engine/feedback", { rating: r, job_id: jobId, comment: note || null });
    } catch (_) {}
  };
  const pick = (r) => {
    setRating(r);
    send(r);
    toast.success("Thanks — your feedback trains the AI Design Brain.");
  };
  const sendNote = async () => {
    await send(rating, comment.trim());
    setDone(true);
  };

  if (done) {
    return (
      <p data-testid="brain-feedback-done" className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-emerald-400">
        <CheckCircle size={14} weight="fill" /> Feedback recorded — thank you!
      </p>
    );
  }
  return (
    <div data-testid="brain-feedback" className="mt-4 flex flex-col items-center gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-zinc-500">Rate this design</span>
        <button
          data-testid="brain-feedback-up"
          onClick={() => pick("up")}
          className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
            rating === "up" ? "bg-emerald-600 text-white" : "bg-white/5 hover:bg-white/15 text-zinc-400 hover:text-white"
          }`}
        >
          <ThumbsUp size={15} weight={rating === "up" ? "fill" : "bold"} />
        </button>
        <button
          data-testid="brain-feedback-down"
          onClick={() => pick("down")}
          className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
            rating === "down" ? "bg-red-600 text-white" : "bg-white/5 hover:bg-white/15 text-zinc-400 hover:text-white"
          }`}
        >
          <ThumbsDown size={15} weight={rating === "down" ? "fill" : "bold"} />
        </button>
      </div>
      {rating && (
        <div className="flex items-center gap-2 w-full max-w-sm">
          <input
            data-testid="brain-feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendNote()}
            placeholder='Optional note, e.g. "Loved the colors" or "Too cluttered"'
            className="flex-1 h-9 rounded-lg bg-black/40 border border-white/10 px-3 text-xs outline-none focus:border-fuchsia-500/60"
          />
          <button
            data-testid="brain-feedback-send"
            onClick={sendNote}
            className="h-9 px-3 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white transition-colors inline-flex items-center gap-1.5 text-xs font-semibold"
          >
            <PaperPlaneTilt size={13} weight="fill" /> Send
          </button>
        </div>
      )}
    </div>
  );
};
